import React from "react";
import withRoot from "../withRoot";
import Layout from "../components/Layout";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Divider from "@material-ui/core/Divider";
import Img from "gatsby-image";
import { graphql } from "gatsby";
import { withStyles } from "@material-ui/core";
import env from "../data/img/banner.svg";

import Prism from "prismjs";
import "prismjs/themes/prism.css";

const styles = {
  icon: {
    fontSize: 20
  },
  infoBlock: {
    maxWidth: 500,
    marginBottom: 20
  },
  paper: {
    maxWidth: 840,
    padding: 16
  }
};

class Index extends React.Component {
  componentDidMount() {
    Prism.highlightAll();
  }

  render() {
    const { classes, data } = this.props;
    return (
      <Layout showHeader={true}>
        <Grid container alignItems="center" justify="center" spacing={24}>
          <Grid container item alignItems="center">
            <Grid item md={4}>
              <Img
                fluid={data.tf.childImageSharp.fluid}
                objectFit="cover"
                objectPosition="50% 50%"
                alt="tf-logo"
              />
            </Grid>
            <Grid item md={8}>
              <Paper className={classes.paper}>
                <Typography variant="h3" gutterBottom>
                  Using tensorflow.js
                </Typography>
                <Divider variant="middle" />
                <Typography variant="body1" component="div" align="left">
                  <p>
                    We can use{" "}
                    <a href="https://www.tensorflow.org/js/">tensorflow.js</a>{" "}
                    to create complex agents directly in the browser. For
                    example, we can create a simple{" "}
                    <a href="https://en.wikipedia.org/wiki/Artificial_neural_network">
                      neural network
                    </a>{" "}
                    that we can use to predict actions given observations using
                    the following code. This code uses the example of the
                    FrozenLake environment.
                  </p>
                  <pre>
                    <code className="language-js">
                      {`let inputSize = 16;   // the frozenlake 4x4 env has 16 different observations
let outputSize = 4;   // the action can be up, down, left or right
let hiddenSize = 50;  // choose a number of "hidden" units for the network
let agent = tf.Sequential();
agent.add(tf.layers.dense({inputShape: [inputSize], units: hiddenSize,
                                  useBias: true, activation: "relu"}));
agent.add(tf.layers.dense({units: hiddenSize, useBias: true, activation: "relu"}));
agent.add(tf.layers.dense({units: outputSize}));
`}
                    </code>
                  </pre>
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          <Grid container item alignItems="center">
            <Grid item md={8}>
              <Paper className={classes.paper}>
                <Typography variant="h3" gutterBottom>
                  Interacting with the Environment
                </Typography>
                <Divider variant="middle" />
                <Typography variant="body1" component="div" align="left">
                  <p>
                    Each environment has the same API. When opening an
                    environment, the environment object is made available
                    globally under the <code>env</code> variable. To inspect
                    variables and their contents, simply log them to the
                    console.
                  </p>
                  <pre>
                    <code className="language-js">console.log(env);</code>
                  </pre>
                  <p>
                    Initially you should call{" "}
                    <code className="language-js">env.reset()</code>, to start the
                    environment. This method returns an initial observation.
                  </p>
                  <pre>
                    <code className="language-js">let obs = env.reset();</code>
                  </pre>
                  <p>
                    Once you've made an observation, your agent should select an
                    action. A list of possible actions is shown in the
                    documentation for each environment. To apply the action to
                    the environment, call{" "}
                    <code className="language-js">env.step(action)</code>
                    and pass your action as an argument to the step method. This
                    method returns a tuple containing the next observation, the
                    reward from the step, a boolean showing if the environment
                    has reached the end, and an object possibly containing some
                    debugging information about the game.
                  </p>
                  <pre>
                    <code className="language-js">
                      {`let action = env.action_space.sample();   // Sample a random action \nlet [obs, rew, done, info] = env.step(action);`}
                    </code>
                  </pre>
                </Typography>
              </Paper>
            </Grid>
            <Grid item md={4} style={{maxWidth: 420}}>
              <img src={env} alt="Interacting with an environment."/>
            </Grid>
          </Grid>
        </Grid>
      </Layout>
    );
  }
}

export const query = graphql`
  query {
    tf: file(relativePath: { eq: "tf-logo.png" }) {
      childImageSharp {
        fluid(maxWidth: 800) {
          ...GatsbyImageSharpFluid_withWebp_tracedSVG
        }
      }
    }
  }
`;

export default withRoot(withStyles(styles)(Index));
